import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import PreviewIcon from '@mui/icons-material/Visibility';
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import TextField from "@mui/material/TextField";
import { tripEnumObject } from "utils/enumObject";
import { truncateComment } from "utils/utils";
import { debounce } from "lodash";
import location from "assets/images/location.svg";
import "./CommonAutoComplete.scss";
import { getLocationCheck, getClearLocationCheck } from "redux/actions/UserGoogleMap";
import PackageService from "services/PackageService";
import PlacardModal from 'components/placardModal/PlacardModal';

function CommonAutoComplete(props) {
  const dispatch = useDispatch();
  const token = props?.token;
  const type = props?.type;
  const [isDisabled, setIsDisabled] = useState(true);
  const [selectedValue, setSelectedValue] = useState({});
  const [openDateTimePicker, setOpenDateTimePicker] = useState(false);
  const [pickup, setPickup] = useState({});
  const [drop, setDrop] = useState({});
  const [optionData, setOptionData] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [airportData, setAirportData] = useState(
    props?.airportOptionData || props?.timeSlabData || []
  );
  const [date, setDate] = useState(
    props?.defaultTripValues["date"] && props?.defaultTripValues["date"]
  );
  const [endDate, setEndDate] = useState(
    props?.defaultTripValues["endDate"] && props?.defaultTripValues["endDate"]
  );
  const [dropValue, setDropValue] = useState(null);

  // Placard state from parent (Home.js / CommonRideView.js)
  const needPlacard = props?.needPlacard || false;
  const setNeedPlacard = props?.setNeedPlacard || (() => {});
  const placardText = props?.placardText || '';
  const setPlacardText = props?.setPlacardText || (() => {});
  const [placardError, setPlacardError] = useState('');
  const [previewModal, setPreviewModal] = useState(false);
  const [selectedPlacardText, setSelectedPlacardText] = useState('');
  const showPlacardOption = type === tripEnumObject?.rental || type === tripEnumObject?.airport;

  const currentDate = new Date();
  const bufferDateTime = (second) => {
    setDate(
      dayjs(
        currentDate.setMinutes(
          Math.ceil(currentDate.getMinutes() / 10) * 10 + second / 60
        )
      )
    );
    setValue(
      "date",
      dayjs(
        currentDate.setMinutes(
          Math.ceil(currentDate.getMinutes() / 10) * 10 + second / 60
        )
      )
    );

    setEndDate(
      dayjs(
        currentDate.setMinutes(
          Math.ceil(currentDate.getMinutes() / 10) * 10 + 3600 / 60
        )
      )
    );
    setValue(
      "endDate",
      dayjs(
        currentDate.setMinutes(
          Math.ceil(currentDate.getMinutes() / 10) * 10 + 3600 / 60
        )
      )
    );
  };

  const locationResponse = useSelector(
    (state) => state.userLocationCheck?.userLocationCheck
  );

 useEffect(() => {
    const fetchPackages = async () => {
      if (type === tripEnumObject.rental) {
        setPackagesLoading(true);
        try {
          const packages = await PackageService.getPackages();
          setOptionData(packages);
        } catch (error) {
          console.error("Failed to fetch packages:", error);
          setOptionData([]);
        } finally {
          setPackagesLoading(false);
        }
      }
    };

    fetchPackages();
  }, [type]);


  useEffect(() => {
    setAirportData(props?.airportOptionData || props?.timeSlabData);
  }, [props?.airportOptionData, props?.timeSlabData]);

  const {
    register,
    handleSubmit,
    clearErrors,
    setValue,
    control,
    formState: { isValid, errors },
  } = useForm({ defaultValues: props?.defaultTripValues, mode: "onChange" });

  useEffect(() => {
    if (props?.pageType === "commonRideTab") {
      if (props?.defaultTripValues?.["pickup"]?.address_components) {
        setValue("pickup", props?.defaultTripValues?.["pickup"]);
        setPickup(props?.defaultTripValues?.["pickup"]);
      }
    } else if (props?.pageType === "homeForm") {
      setDate(dayjs(props?.defaultTripValues?.["date"]?.$d));
      setEndDate(dayjs(props?.defaultTripValues?.["endDate"]?.$d));
    }
    if (props?.defaultTripValues?.package) {
      setSelectedValue(props?.defaultTripValues);
    }
  }, [props?.defaultTripValues]);

  useEffect(() => {
    if (type) {
      bufferDateTime(
        props?.timeSlabData?.bookingBuffer ||
          props?.timeSlabData ||
          props?.bufferTime ||
          600
      );
    }
  }, [
    type,
    props?.timeSlabData?.bookingBuffer,
    props?.timeSlabData,
    props?.bufferTime,
  ]);

  const handleInput = (e, value) => {
    const rentalObject = optionData?.filter(
      (item) => item.id === e.target.value
    )[0];
    
    // Store with rateCardTypeId instead of slabId
    const packageData = {
      ...rentalObject,
      rateCardTypeId: rentalObject?.id
    };
    
    sessionStorage.setItem("package", JSON.stringify(packageData));
    if (pickup) {
      setIsDisabled(false);
    }
    clearErrors(e.target.name);
    let data = {};
    if (value && value !== undefined) {
      data[e.target.name] = value;
    } else {
      data[e.target.name] = e.target.value;
    }
    if (rentalObject) {
      setSelectedValue(rentalObject);
    }
    props?.handleInput(e, value);
  };
  const onInputChangeVal = debounce((event, value, name) => {
    if (value?.length < 1) {
      sessionStorage.removeItem(name);
    }
    if (event?.type === "change" && value?.length) {
      props?.onInputChangeVal(event, value, name);
    }
  }, 300);

  useEffect(() => {
    if (pickup?.place_id && drop?.place_id && date?.$d) {
      if (
        (type === tripEnumObject.outStationRound && endDate?.$d) ||
        type !== tripEnumObject.outStationRound
      ) {
        setIsDisabled(false);
      }
    }
  }, [pickup?.place_id, drop?.place_id, date?.$d, endDate?.$d]);

  useEffect(() => {
    if(type === tripEnumObject.outStationRound) {
    if (locationResponse?.success) {
      setIsDisabled(false);
    }}
  }, [locationResponse,date?.$d, endDate?.$d]); 

  const getOptionLabel = (option) => {
    if (option?.description) {
      return option?.description;
    } else if (option?.formatted_address) {
      return option?.formatted_address;
    } else if (option?.vicinity) {
      return option?.name + "," + option?.vicinity;
    } else {
      return "";
    }
  };

  const optionsData = (name) => {
    if (name === "pickup" || type == tripEnumObject?.outStationRound) {
      return props?.locationData;
    } else if (name === "drop") {
      return props?.nearbySearchData;
    }
  };

  const setValueSession = (name, value) => {
    setValue(name, value);
    sessionStorage.setItem(name, JSON.stringify(value));
  };

  const onHandleChange = (event, value, name) => {
    clearErrors(event.target.name);
    dispatch(getClearLocationCheck());
    if (props?.onHandleChange && token) {
      props?.onHandleChange(event, value, name);
    } else if (
      props?.onHandleChange &&
      (props?.type === tripEnumObject?.rental ||
        props?.type === tripEnumObject?.airport ||
        !token)
    ) {
      props?.onHandleChange(event, value, name);
      if (name === "pickup") {
        setPickup(value);
        setValueSession(name, value);
      } else if (name === "drop") {
        if (props?.type === tripEnumObject?.outStationRound) {
          const payload = {
            sourceAddress: pickup?.description,
            destinationAddress: value?.description,
            type: props?.type,
            location: "",
          };
          dispatch(getLocationCheck(payload));
        }
        setDrop(value);
        setValueSession(name, value);
      } else if (name === "date") {
        setDate(value);
        setValue(name, value);
      }
    }
  };

  const onChangeDate = (e, name) => {
    if (!isNaN(e?.$D) && name === "date") {
      setDate(dayjs(e));
      props?.onChangeDate(e, name);
    }
    if (!isNaN(e?.$D) && name === "endDate") {
      setEndDate(dayjs(e));
      props?.onChangeDate(e, name);
    }
  };

  const handleOpen = (fieldName) => {
    setOpenDateTimePicker(fieldName);
  };

  const handleClose = () => {
    setOpenDateTimePicker(null);
  };

  const handleOverlayClick = (fieldName) => {
    handleOpen(fieldName);
  };

  const onCurrentAddress = () => {
    props?.onCurrentAddress();
  };

  const handlePlacardTextChange = (e) => {
    const val = e.target.value;
    setPlacardText(val);
    // Keep sessionStorage in sync for ConfirmPay and rate calculation
    if (val) {
      sessionStorage.setItem("placardText", val);
    } else {
      sessionStorage.removeItem("placardText");
    }
    if (val) {
      const lines = val.split('\n');
      if (lines.some(line => line.length > 25)) {
        setPlacardError('Maximum 25 characters per line allowed');
      } else {
        setPlacardError('');
      }
    } else {
      setPlacardError('');
    }
  };

  const handlePlacardPreview = () => {
    if (placardText) {
      setSelectedPlacardText(placardText);
      setPreviewModal(true);
    }
  };

  const handleNeedPlacardChange = (e) => {
    setNeedPlacard(e.target.checked);
    if (e.target.checked) {
      sessionStorage.setItem("needPlacard", "true");
    } else {
      setPlacardText('');
      setPlacardError('');
      sessionStorage.removeItem("needPlacard");
      sessionStorage.removeItem("placardText");
    }
  };

  const onSubmit = (data, code) => {
    let dataVal = data;
    if (token) {
      sessionStorage.setItem("pickup", JSON.stringify(pickup));
      sessionStorage.setItem("drop", JSON.stringify(drop));
    } else if (pickup && drop) {
      sessionStorage.setItem(
        "pickup",
        JSON.stringify(
          pickup?.description ? pickup : props?.defaultTripValues?.pickup
        )
      );
      sessionStorage.setItem("drop", JSON.stringify(drop));
      const date = data?.date?.$d;
      sessionStorage.setItem("date", date.toISOString());
    }
    // Store placard in sessionStorage for API calls and as fallback for remounts
    if (showPlacardOption && needPlacard && placardText) {
      sessionStorage.setItem("needPlacard", "true");
      sessionStorage.setItem("placardText", placardText);
    } else {
      sessionStorage.removeItem("needPlacard");
      sessionStorage.removeItem("placardText");
    }
    props?.onSubmitBtn(dataVal, code);
  };

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container>
          {props?.formData?.length > 0 &&
            props?.formData?.map((ele, idx) => (
              <Grid  item
                xs={12}
                sm={8}
                key={idx}
                sx={{
                  width: { xs: "100%", sm: "auto" }, // full width on mobile
                }}>
                <FormControl variant="standard" className="width100">
                  {ele?.autoComplete ? (
                    <Stack xs={12}>
                      {props?.currentLoader && ele?.name === "pickup" ? (
                        <CircularProgress color="inherit" />
                      ) : (
                        <Autocomplete
                          id="free-solo-demo"
                          disableClearable
                          freeSolo
                          defaultValue={
                            props?.defaultTripValues[ele?.name]
                              ? props?.defaultTripValues[ele?.name]
                              : null
                          }
                          getOptionLabel={(option) => getOptionLabel(option)}
                          options={optionsData(ele?.name) || []}
                          disabled={ele?.disabled || false}
                          onChange={(event, value) => {
                            setValue(ele?.name, value);
                            onHandleChange(event, value, ele?.name);
                          }}
                          onInputChange={(e, value) =>
                            onInputChangeVal(e, value, ele?.name)
                          }
                          renderOption={(props, option) => (
                            <li {...props}>
                              <Grid
                                container
                                className="spacingLeft"
                                spacing={1}
                              >
                                <Grid item className="displayNone">
                                  <img src={location} alt="location" />
                                </Grid>
                                <Grid item justifyContent="start">
                                  <Typography className="font14 fontNormal">
                                    {truncateComment(
                                      option?.name
                                        ? option?.name
                                        : option?.structured_formatting
                                            ?.main_text,
                                      50
                                    )}
                                  </Typography>
                                  <Typography className="font12 colorGrey">
                                    <Tooltip
                                      title={option?.description}
                                      placement="bottom"
                                    >
                                      {truncateComment(
                                        option?.vicinity
                                          ? option?.name +
                                              "," +
                                              option?.vicinity
                                          : option?.description,
                                        50
                                      )}
                                    </Tooltip>
                                  </Typography>
                                </Grid>
                              </Grid>
                            </li>
                          )}
                          renderInput={(params) => (
                            <Box className="dflex">
                              <div
                                className={ele?.border ? "pickup" : null}
                              ></div>
                              <img
                                className="wh33 marginRight10"
                                src={ele?.image}
                                alt="pickupicon"
                              />
                              {params.InputProps.startAdornment}
                              <TextField
                                {...params}
                                margin="normal"
                                className="inputStyle"
                                placeholder={ele?.placeholder}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      {ele?.icon ? (
                                        <img
                                          src={ele?.icon}
                                          alt="location"
                                          onClick={() => onCurrentAddress()}
                                          className="cursor"
                                        />
                                      ) : null}
                                    </InputAdornment>
                                  ),
                                }}
                                {...register(ele?.name, {
                                  required: true,
                                })}
                              />
                            </Box>
                          )}
                        />
                      )}

                      {errors[ele?.name] && (
                        <Grid item>
                          <Typography className="errorMessages">
                            This field is required.
                          </Typography>
                        </Grid>
                      )}
                    </Stack>
                  ) : ele?.type === "options" ? (
                    <FormControl>
                      <Box className="marginBlock10 dflex alignItem">
                        <img
                          src={ele?.image}
                          alt="pickupicon"
                          className="marginRight10 wh33"
                        />
                        <Box className="width100">
                          {packagesLoading ? (
                            <Box className="dflex alignItem justifyCenter p-4">
                              <CircularProgress size={24} />
                              <Typography className="ml-2">Loading packages...</Typography>
                            </Box>
                          ) : (
                            <Select
                              value={
                                props?.defaultTripValues?.package?.id ||
                                props?.rentalPackage
                              }
                              className="inputStyle width100"
                              IconComponent={(params) => (
                                <ExpandMoreRoundedIcon
                                  className="marginRight10"
                                  {...params}
                                />
                              )}
                              disabled={props?.disabled || ele?.disabled || packagesLoading}
                              {...register(ele?.name, {
                                required: ele?.required || false,
                              })}
                              onChange={(e) => handleInput(e)}
                              displayEmpty
                              inputProps={{
                                "aria-label": "Without label",
                              }}
                            >
                              <MenuItem value="" disabled>
                                <em>{ele?.placeholder || "Select Package"}</em>
                              </MenuItem>
                              {optionData?.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                  {option?.label}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                          {errors[ele?.name] && (
                            <Typography className="colorRed">
                              This field is required.
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </FormControl>
                  ) : ele?.type === "date" ? (
                      <Grid item container className="marginBlock10">
                        <Grid item
                          xs={12}
                          sm={8}
                          sx={{
                            width: { xs: "100%", sm: "auto" }, // increase width in mobile
                          }}>
                          <Box className="alignItem gap-2">
                            <img
                              src={ele?.image}
                              alt="pickupicon"
                              className="marginRight16 wh24"
                            />
                            <Controller
                              name={ele?.name}
                              control={control}
                              rules={{ required: "Date is required" }}
                              render={({ field }) => (
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                  <div className="dateTimePickerContainer">
                                    <DateTimePicker
                                      {...field}
                                      views={["year", "month", "day", "hours", "minutes"]}
                                      open={openDateTimePicker === field.name}
                                      onOpen={() => handleOpen(field.name)}
                                      onClose={() => handleClose()}
                                      minutesStep={2}
                                      ampm={false}
                                      value={ele?.name === "date" ? date : endDate}
                                      disabled={ele?.disabled || false}
                                      onChange={(e) => {
                                        setValue(ele?.name, e);
                                        onChangeDate(e, ele?.name);
                                      }}
                                      defaultValue={dayjs(date) || null}
                                      minDateTime={dayjs()} // Only allow future date/time
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          placeholder={
                                            ele?.name === "date"
                                              ? "Start Date & Time"
                                              : "Return Date & Time"
                                          }
                                          fullWidth
                                          size="small"
                                          className="inputStyle width100"
                                        />
                                      )}
                                      minDate={dayjs()} // Only allow future date
                                    />
                                    <div
                                      className="clickableOverlay"
                                      onClick={() => handleOverlayClick(ele?.name)}
                                    ></div>
                                  </div>
                                </LocalizationProvider>
                              )}
                            />
                          </Box>
                        </Grid>

                        {errors[`date`] && !date && (
                          <Typography className="errorMessages">
                            {errors[`date`].message}
                          </Typography>
                        )}
                      </Grid>
                    ) :
                  ele?.singleAutoComplete ? (
                    <Stack xs={12}>
                      <Autocomplete
                        id="free-solo-demo"
                        disableClearable
                        defaultValue={
                          props?.defaultTripValues[ele?.name]
                            ? props?.defaultTripValues[ele?.name]
                            : ""
                        }
                        isOptionEqualToValue={(option, value) =>
                          option?.id === value?.id || option?.place_id === value?.place_id
                        }
                        getOptionLabel={(option) =>
                          option.label || option.name || ""
                        }
                        options={airportData}
                        onChange={(event, value) =>
                          onHandleChange(event, value, ele?.name)
                        }
                        onInputChange={(e, value) =>
                          onInputChangeVal(e, value, ele?.name)
                        }
                        filterSelectedOptions
                        renderOption={(props, option) => (
                          <li {...props}>
                            <Grid
                              container
                              className="spacingLeft"
                              key={option?.id}
                            >
                              <Grid item>
                                <img src={location} alt="location" />
                              </Grid>
                              <Grid
                                item
                                justifyContent="start"
                                className="marginLeft16 width375"
                              >
                                <Typography className="font14 fontNormal">
                                  {option?.label || option?.name}
                                </Typography>
                              </Grid>
                            </Grid>
                          </li>
                        )}
                        renderInput={(params) => (
                          <Box className="dflex">
                            <div
                              className={ele?.border ? "pickup" : null}
                            ></div>
                            <img
                              className="wh33 marginRight10"
                              src={ele?.image}
                              alt="pickupicon"
                            />
                            {params.InputProps.startAdornment}
                            <TextField
                              {...params}
                              margin="normal"
                              className="inputStyle"
                              placeholder={ele?.placeholder}
                              inputProps={{
                                ...params.inputProps,
                                autoComplete: "disabled",
                              }}
                              {...register(ele?.name, {
                                required: true,
                              })}
                            />
                          </Box>
                        )}
                      />
                      {errors[ele?.name] && (
                        <Typography className="errorMessages">
                          This field is required.
                        </Typography>
                      )}
                    </Stack>
                  ) : null}
                </FormControl>
              </Grid>
            ))}
        </Grid>

        {/* Placard - only for Rental and Airport trips */}
        {showPlacardOption && (
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={needPlacard}
                  onChange={handleNeedPlacardChange}
                />
              }
              label="Need Placard"
            />
            {needPlacard && (
              <Box sx={{ mt: 1, maxWidth: '50%' }}>
                <Typography
                  component="label"
                  sx={{ display: 'flex', alignItems: 'center', mb: 0.5, fontSize: '14px', fontWeight: 500, color: '#374151' }}
                >
                  Placard Text
                  <Tooltip title="Preview Placard">
                    <Button
                      variant="text"
                      size="small"
                      onClick={handlePlacardPreview}
                      sx={{
                        minWidth: '40px',
                        '& .MuiButton-startIcon': { margin: 0 },
                      }}
                    >
                      <PreviewIcon fontSize="small" />
                    </Button>
                  </Tooltip>
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  value={placardText}
                  onChange={handlePlacardTextChange}
                  placeholder="Enter Placard Text (25 characters per line)"
                  variant="outlined"
                  error={!!placardError}
                  helperText={placardError}
                  inputProps={{ maxLength: 50 }}
                  className="bg-white"
                />
              </Box>
            )}
          </Box>
        )}

        {props?.btnEnable === true || !props?.token ? (
                 <Box  
                 sx={{
                  display: "flex",
                  justifyContent: { xs: "center", sm: "flex-start" }, // center on mobile, left on desktop
                }}>
                  <Button
                    variant="contained"
                    size="large"
                    type="submit"
                    className={`w-[68%]
                    ${isDisabled ? "buttonSubmitDisabled" : "buttonSubmit"}
                    `}
                    disabled={isDisabled}
                  >
                  Book a cab
                  </Button>
                  </Box>
        ) : null}

        {/* Placard Preview Modal */}
        {previewModal && (
          <PlacardModal
            open={previewModal}
            handleClose={() => setPreviewModal(false)}
            text={selectedPlacardText}
          />
        )}
      </form>
    </div>
  );
}
export default CommonAutoComplete;